import React, {FC} from 'react';

import {CreateAccountFooterContainer} from '../../../../../../src/components/accounts/createAccount/createAccountFooterContainer';
import {CreateAccountFormContainer} from '../../../../../../src/components/accounts/createAccount/createAccountFormContainer';
import {TransientContactDetailsContainer} from '../../../../../../src/components/contacts/contactDetailsContainer';
import {SidePanelHeader} from '../../../../../../src/components/inboxes/side/sidePanelHeader';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../src/services/tracking';
import {ContactDetailsPanelLocationsEnum} from '../../../../../../src/store/panels/contacts/contactDetailsPanelState';
import {
  ContactsManagerParameters,
  ContactsManagerParametersTypesEnum,
} from '../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {ContactsManagerSearchState} from '../../../../../../src/store/panels/contacts/contactsManager/search/contactsManagerSearchState';
import {
  ContactsManagerSidePanelState,
  sidePanelIsAccountDetails,
  sidePanelIsCreateAccountForm,
  sidePanelIsCreateContactForm,
  sidePanelIsMergeContactsForm,
} from '../../../../../../src/store/panels/contacts/contactsManager/sidePanel/contactsManagerSidePanelState';
import {
  ContactsManagerPanelId,
  makeContactDetailsPanelId,
} from '../../../../../../src/store/panels/panelStateBase';
import {Trans} from '../../../../../shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../shared/helpers/src/react/reactHelpers';
import {useContactsManagerContext} from './contactsManagerContext';
import {CreateContactFooterContainer} from './createContact/createContactFooterContainer';
import {CreateContactFormContainer} from './createContact/createContactFormContainer';
import {
  buildContactsManagerDeleteContactEvent,
  ContactsManagerContactDetailsTrackingHandler,
  ContactsManagerTrackingHandler,
} from './helpers/trackingHelpers';
import {AccountDetailsSidePanelContainer} from './sidePanel/accountDetails/accountDetailsSidePanelContainer';
import {ContactsManagerSidePanelWrapperContainer} from './sidePanel/contactsManagerSidePanelWrapperContainer';
import {MergeContactsSidePanelContainer} from './sidePanel/merge/mergeContactsSidePanelContainer';

/*
 * Types.
 */

interface ContactsManagerSidePanelProps {
  sidePanelState: ContactsManagerSidePanelState | undefined;
  searchState?: ContactsManagerSearchState;
  parameters: ContactsManagerParameters;
  closeSidePanel: Handler;
  onCloseContactDetails: Handler;
  onCardDelete: HandlerOf<number>;
}

/*
 * Component.
 */

export const ContactsManagerSidePanel: FC<ContactsManagerSidePanelProps> = (props) => {
  const {sidePanelState, parameters, searchState, closeSidePanel, onCloseContactDetails, onCardDelete} =
    props;
  const {panelId} = useContactsManagerContext();

  return (
    <>
      {renderSidePanelContent(panelId, sidePanelState, parameters, closeSidePanel)}
      {maybeRenderContactDetailsPanel(panelId, parameters, searchState, onCloseContactDetails, onCardDelete)}
    </>
  );
};

/*
 * Helpers.
 */

const renderSidePanelContent = (
  panelId: ContactsManagerPanelId,
  sidePanelState: ContactsManagerSidePanelState | undefined,
  parameters: ContactsManagerParameters,
  closeSidePanel: Handler,
) => {
  if (!sidePanelState) {
    return null;
  }

  if (sidePanelIsCreateContactForm(sidePanelState)) {
    return (
      <ContactsManagerSidePanelWrapperContainer shouldCoverContent>
        <SidePanelHeader
          title={<Trans id="contacts-manager-new-contact">New contact</Trans>}
          onClose={closeSidePanel}
        />
        <CreateContactFormContainer panelId={panelId} createContactFormState={sidePanelState} />
        <CreateContactFooterContainer
          panelId={panelId}
          createContactFormState={sidePanelState}
          onTrackEvent={(event) => ContactsManagerTrackingHandler(parameters.type, event)}
        />
      </ContactsManagerSidePanelWrapperContainer>
    );
  }

  if (sidePanelIsCreateAccountForm(sidePanelState)) {
    return (
      <ContactsManagerSidePanelWrapperContainer shouldCoverContent>
        <SidePanelHeader
          title={<Trans id="contacts-manager-new-account">New account</Trans>}
          onClose={closeSidePanel}
        />
        <CreateAccountFormContainer panelId={panelId} createAccountFormState={sidePanelState} />
        <CreateAccountFooterContainer panelId={panelId} createAccountFormState={sidePanelState} />
      </ContactsManagerSidePanelWrapperContainer>
    );
  }

  if (sidePanelIsAccountDetails(sidePanelState)) {
    return (
      <ContactsManagerSidePanelWrapperContainer>
        <AccountDetailsSidePanelContainer panelId={panelId} />
      </ContactsManagerSidePanelWrapperContainer>
    );
  }

  if (sidePanelIsMergeContactsForm(sidePanelState)) {
    return (
      <ContactsManagerSidePanelWrapperContainer shouldCoverContent>
        <MergeContactsSidePanelContainer
          onMergeSuccess={() => {
            trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACT_MERGE, {
              num_merged: sidePanelState.cardIds.length,
              section_type: parameters.type,
            });
          }}
        />
      </ContactsManagerSidePanelWrapperContainer>
    );
  }

  return null;
};

function maybeRenderContactDetailsPanel(
  panelId: ContactsManagerPanelId,
  parameters: ContactsManagerParameters,
  searchState: ContactsManagerSearchState | undefined,
  onCloseContactDetails: Handler,
  onCardDelete: HandlerOf<number>,
) {
  const cardId = computeCardIdForContactDetails(parameters, searchState);
  if (!cardId) {
    return null;
  }
  return (
    <ContactsManagerSidePanelWrapperContainer>
      <TransientContactDetailsContainer
        panelId={makeContactDetailsPanelId(panelId)}
        selectedCardId={cardId}
        location={ContactDetailsPanelLocationsEnum.CONTACTS_MANAGER}
        onClose={onCloseContactDetails}
        onDelete={(card) => {
          onCardDelete(card.id);
          if (card) {
            const event = buildContactsManagerDeleteContactEvent(1, [card.namespace]);
            ContactsManagerTrackingHandler(parameters.type, event);
          }
        }}
        onTrackEvent={(event) => ContactsManagerContactDetailsTrackingHandler(parameters.type, event)}
      />
    </ContactsManagerSidePanelWrapperContainer>
  );
}

function computeCardIdForContactDetails(
  parameters: ContactsManagerParameters,
  searchState?: ContactsManagerSearchState,
) {
  if (
    parameters.type === ContactsManagerParametersTypesEnum.SHARED_CONTACTS ||
    parameters.type === ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS ||
    parameters.type === ContactsManagerParametersTypesEnum.AUTO_CONTACTS ||
    parameters.type === ContactsManagerParametersTypesEnum.LIST_CONTACTS ||
    parameters.type === ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS
  ) {
    return parameters.cardId;
  }
  if (searchState?.contactsTableState) {
    if (
      searchState.contactsTableState.isInMultiSelectMode ||
      searchState.contactsTableState.selectedCardIds.length !== 1
    ) {
      return null;
    }
    return searchState.contactsTableState.selectedCardIds[0];
  }
  return null;
}
